import type { UseFormReturn } from 'react-hook-form';
import { Section } from '../../../components/ui/Section';
import { Badge } from '../../../components/ui/Badge';
import { SelectWithCreate } from '../../../components/ui/InlineCreate';
import { asRecord, str } from '../../../platform/api/utils';
import {
  type EditForm,
  STATUS_LABELS,
  STATUS_VARIANTS,
  VISIBILITY_LABELS,
  Field,
  dlGrid,
  Dt,
  Dd,
  inputCss,
  primaryBtn,
  secondaryBtn,
} from './inventory-detail.support';

function fmtDate(v: unknown): string {
  if (typeof v === 'string' && v.length >= 10) return v.substring(0, 10);
  if (v instanceof Date) return v.toISOString().substring(0, 10);
  return '—';
}
interface MutateAsync {
  mutateAsync: (arg: string) => Promise<unknown>;
  isPending: boolean;
}
export function InventoryDadosTab(props: {
  editing: boolean;
  form: UseFormReturn<EditForm>;
  it: Record<string, unknown>;
  st: string;
  vis: string;
  cat: Record<string, unknown> | undefined;
  loc: Record<string, unknown> | undefined;
  categories: Record<string, unknown>[];
  locations: Record<string, unknown>[];
  createCategory: MutateAsync;
  createLocation: MutateAsync;
  updateItem: { isPending: boolean };
  onCancelEdit: () => void;
  onSave: (data: EditForm) => void;
}) {
  const {
    editing,
    form,
    it,
    st,
    vis,
    cat,
    loc,
    categories,
    locations,
    createCategory,
    createLocation,
    updateItem,
    onCancelEdit,
    onSave,
  } = props;
  if (editing) {
    return (
      <Section title="Editar item">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
          <Field label="Nome" error={form.formState.errors.name?.message}>
            <input {...form.register('name')} style={inputCss} />
          </Field>
          <Field label="Unidade">
            <input {...form.register('unit')} style={inputCss} />
          </Field>
          <Field label="Quantidade atual">
            <div style={{ fontSize: '0.875rem', color: 'var(--muted)', padding: '0.5rem 0' }}>
              <strong>{str(it.currentQuantity)}</strong> {str(it.unit)}
              <span
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  color: 'var(--muted)',
                  marginTop: 4,
                }}
              >
                O saldo só muda por movimentação (entrada, saída, ajuste ou perda).
              </span>
            </div>
          </Field>
          <Field label="Quantidade mínima">
            <input
              {...form.register('minimumQuantity')}
              style={inputCss}
              type="number"
              step="any"
            />
          </Field>
          <SelectWithCreate
            label="Categoria"
            value={form.watch('categoryId') ?? ''}
            onChange={(cid) => {
              form.setValue('categoryId', cid);
            }}
            options={categories.map((c) => ({ id: str(c.id), name: str(c.name) }))}
            createLabel="+ Nova categoria"
            createPlaceholder="Nome da categoria"
            creating={createCategory.isPending}
            onCreate={async (name) => {
              const created = await createCategory.mutateAsync(name);
              return str(asRecord(created).id);
            }}
          />
          <SelectWithCreate
            label="Localização"
            value={form.watch('locationId') ?? ''}
            onChange={(lid) => {
              form.setValue('locationId', lid);
            }}
            options={locations.map((l) => ({ id: str(l.id), name: str(l.name) }))}
            createLabel="+ Nova localização"
            createPlaceholder="Nome do local"
            creating={createLocation.isPending}
            onCreate={async (name) => {
              const created = await createLocation.mutateAsync(name);
              return str(asRecord(created).id);
            }}
          />
          <Field label="Visibilidade">
            <select {...form.register('visibility')} style={inputCss}>
              {Object.entries(VISIBILITY_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Validade">
            <input {...form.register('expiryDate')} type="date" style={inputCss} />
          </Field>
          <Field label="Descrição" error={undefined}>
            <textarea
              {...form.register('description')}
              style={{ ...inputCss, minHeight: 60, resize: 'vertical' }}
            />
          </Field>
          <div>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}
            >
              <input type="checkbox" {...form.register('isPerishable')} />
              Perecível
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.8rem',
                marginTop: '0.3rem',
              }}
            >
              <input type="checkbox" {...form.register('requiresHouseValidation')} />
              Requer validação da casa
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.8rem',
                marginTop: '0.3rem',
              }}
            >
              <input type="checkbox" {...form.register('controlledOrSensitive')} />
              Controlado ou sensível
            </label>
          </div>
        </div>
        <div
          style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}
        >
          <button type="button" onClick={onCancelEdit} style={secondaryBtn}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              void form.handleSubmit(onSave)();
            }}
            disabled={updateItem.isPending}
            style={primaryBtn}
          >
            {updateItem.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </Section>
    );
  }
  return (
    <>
      <Section title="Dados do item">
        <dl style={dlGrid}>
          <Dt>Nome</Dt>
          <Dd>{str(it.name)}</Dd>
          <Dt>Categoria</Dt>
          <Dd>{cat ? str(cat.name) : '—'}</Dd>
          <Dt>Localização</Dt>
          <Dd>{loc ? str(loc.name) : '—'}</Dd>
          <Dt>Unidade</Dt>
          <Dd>{str(it.unit)}</Dd>
          <Dt>Quantidade atual</Dt>
          <Dd>
            <span style={{ fontWeight: 600, fontSize: '1rem' }}>{str(it.currentQuantity)}</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)', marginLeft: '0.3rem' }}>
              {str(it.unit)}
            </span>
          </Dd>
          <Dt>Estoque mínimo</Dt>
          <Dd>{it.minimumQuantity ? str(it.minimumQuantity) : '—'}</Dd>
          <Dt>Situação</Dt>
          <Dd>
            <Badge label={STATUS_LABELS[st] ?? '—'} variant={STATUS_VARIANTS[st] ?? 'default'} />
          </Dd>
          <Dt>Visibilidade</Dt>
          <Dd>{VISIBILITY_LABELS[vis] ?? vis}</Dd>
          <Dt>Validade</Dt>
          <Dd>{fmtDate(it.expiryDate)}</Dd>
          {str(it.description) && (
            <>
              <Dt>Descrição</Dt>
              <Dd>{str(it.description)}</Dd>
            </>
          )}
        </dl>
      </Section>
      <Section title="Classificação">
        <dl style={dlGrid}>
          <Dt>Perecível</Dt>
          <Dd>
            <Badge
              label={it.isPerishable ? 'Sim' : 'Não'}
              variant={it.isPerishable ? 'warning' : 'default'}
            />
          </Dd>
          <Dt>Validação da casa</Dt>
          <Dd>
            <Badge
              label={it.requiresHouseValidation ? 'Sim' : 'Não'}
              variant={it.requiresHouseValidation ? 'info' : 'default'}
            />
          </Dd>
          <Dt>Controlado/sensível</Dt>
          <Dd>
            <Badge
              label={it.controlledOrSensitive ? 'Sim' : 'Não'}
              variant={it.controlledOrSensitive ? 'danger' : 'default'}
            />
          </Dd>
        </dl>
      </Section>
      <Section title="Metadados">
        <dl style={dlGrid}>
          <Dt>Criado em</Dt>
          <Dd>{fmtDate(it.createdAt)}</Dd>
          <Dt>Atualizado em</Dt>
          <Dd>{fmtDate(it.updatedAt)}</Dd>
        </dl>
      </Section>
    </>
  );
}
